import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { failDelivery, DELIVERY_ERRORS } from '../../api/delivery';
import DriverShell from './DriverShell';
import { useDriverOrder } from './useDriverOrder';
import cardStyles from './DriverCards.module.css';
import formStyles from '../../styles/form.module.css';
import styles from './DriverFailDelivery.module.css';

const REASONS = ['Customer unavailable', 'Wrong address', 'Customer refused', 'Other'];

function friendlyError(err) {
  return DELIVERY_ERRORS[err.message] || err.message || 'Could not mark this delivery as failed — try again.';
}

export default function DriverFailDelivery() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useOrders();
  const { order } = useDriverOrder(orderId);
  const [reason, setReason] = useState(REASONS[0]);
  const [otherDetail, setOtherDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOther = reason === 'Other';
  const canConfirm = !isOther || otherDetail.trim().length > 0;

  const handleConfirm = async () => {
    if (!canConfirm || !order) return;
    setSubmitting(true);
    try {
      await failDelivery(order.id, isOther ? otherDetail.trim() : reason, user.idToken);
      notify(`Order ${order.id} marked as failed`);
      navigate('/driver', { replace: true });
    } catch (err) {
      notify(friendlyError(err));
      setSubmitting(false);
    }
  };

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={cardStyles.headerTitle}>Fail Delivery</h1>
      </header>

      {order && (
        <main className={cardStyles.content}>
          <section className={cardStyles.card}>
            <p className={cardStyles.cardLabel}>Why couldn't this delivery be completed?</p>
            <div className={styles.reasonList}>
              {REASONS.map((r) => (
                <label className={styles.reasonRow} key={r}>
                  <input
                    type="radio"
                    name="fail-reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                  />
                  {r}
                </label>
              ))}
            </div>

            {isOther && (
              <textarea
                className={`${formStyles.textarea} ${styles.otherDetail}`}
                placeholder="Describe what happened…"
                value={otherDetail}
                onChange={(e) => setOtherDetail(e.target.value)}
                autoFocus
              />
            )}
          </section>

          <button
            type="button"
            className={styles.confirmBtn}
            disabled={!canConfirm || submitting}
            onClick={handleConfirm}
          >
            {submitting ? 'Confirming…' : 'Confirm Failure'}
          </button>
        </main>
      )}
    </DriverShell>
  );
}
